import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Star, Download, Loader2, Package, Tag, Building2, Filter } from 'lucide-react';
import { marketplaceApi } from '../api';
import DashboardLayout from '../components/dashboard/DashboardLayout';

function BundleCard({ bundle }) {
  const { t } = useTranslation();
  const avgRating = bundle.rating_count > 0 ? (bundle.rating_sum / bundle.rating_count).toFixed(1) : null;
  return (
    <Link to={`/dashboard/marketplace/${bundle.id}`} className="block bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-semibold text-slate-900 flex-1">{bundle.title}</h3>
        {bundle.is_verified && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{t('marketplace.editors_pick')}</span>
        )}
      </div>
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{bundle.description}</p>

      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3 flex-wrap">
        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {bundle.source_org_name}</span>
        <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {bundle.category}</span>
        {bundle.industry && <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {bundle.industry}</span>}
      </div>

      {bundle.tags && bundle.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {bundle.tags.slice(0, 4).map((t, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">#{t}</span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
        <span className="flex items-center gap-1">
          <Download className="w-3 h-3" /> {t('marketplace.installs', { count: bundle.installs_count || 0 })}
        </span>
        {avgRating && (
          <span className="flex items-center gap-1 text-amber-600">
            <Star className="w-3 h-3 fill-current" /> {avgRating} ({bundle.rating_count})
          </span>
        )}
      </div>
    </Link>
  );
}

export default function MarketplacePage() {
  const { t } = useTranslation();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('recent');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const CATEGORIES = [
    { id: '', label: t('marketplace.all_categories') },
    { id: 'pipeline', label: 'Pipelines' },
    { id: 'rules', label: 'Rules' },
    { id: 'prompts', label: 'AI Prompts' },
    { id: 'streams', label: 'Streams' },
    { id: 'dashboard', label: 'Dashboards' },
    { id: 'ai-config', label: 'AI Config' },
    { id: 'full-org-setup', label: 'Full Org Setup' },
    { id: 'industry-template', label: 'Industry Template' },
  ];

  const SORT = [
    { id: 'recent', label: t('marketplace.sort_recent') },
    { id: 'popular', label: t('marketplace.sort_popular') },
    { id: 'top-rated', label: t('marketplace.sort_top_rated') },
    { id: 'trending', label: t('marketplace.sort_trending') },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 50 };
      if (category) params.category = category;
      if (search) params.search = search;
      const r = await marketplaceApi.list(params);
      setBundles(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category, sort, search]);

  const onSearch = (e) => { e.preventDefault(); setSearch(searchInput); };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Package className="w-6 h-6 text-indigo-500" />
          {t('marketplace.title')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t('marketplace.subtitle')}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={t('marketplace.search_placeholder')}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </form>

        <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>

        <select value={sort} onChange={e => setSort(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
          {SORT.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('marketplace.empty_title')}</p>
          <p className="text-xs mt-2">{t('marketplace.empty_subtitle')}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bundles.map(b => <BundleCard key={b.id} bundle={b} />)}
        </div>
      )}
    </DashboardLayout>
  );
}
